import { useState, type ReactNode } from "react";

import { makePostApiCall } from "../../shared/makeApiCall";
import { useToast } from "../../contexts/ToastContext";

import FilterSingleSelectDropdown from "../../components/filterDropdown/FilterSingleSelectDropdown";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import type { EmptyModalState } from "../../components/modals/EmptyModal";
import type { SlideOverState } from "../../components/slideOver/SlideOver";
import type { ApiMessage, SelectOption } from "../../types/api";
import type {
  CategoryFilters,
  ProductCategory,
  ProductCategoriesResponse,
} from "../../types/categories";

export function ProductCategoriesViewModel(logout: () => void) {
  const { showSuccess } = useToast();
  const [emptyModal, setEmptyModelOpen] = useState<EmptyModalState>({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [primaryCategories, setProductCategories] = useState<ProductCategoriesResponse>({
    current_page: 1,
    data: [],
    last_page: 1,
    per_page: 20,
    total: 0,
  });
  const [primaryCategoriesForDropdown, setPrimaryCategoriesForDropdown] = useState<SelectOption[]>(
    []
  );

  const defaultFilters: CategoryFilters = {
    primaryCategory: { value: -1, label: "All Primary Categories" },
  };
  const [filters, setFilters] = useState(defaultFilters);

  const [slideOver, setSlideOver] = useState<SlideOverState>({
    childComponent: null,
    open: false,
  });

  const showErrorModal = (title: string, subtitle = "Please try again.") => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent setOpen={setEmptyModelOpen} subtitle={subtitle} title={title} />
      ),
      open: true,
    });
  };

  const getProductCategories = async function (
    search: string | null,
    page: number,
    filter: CategoryFilters
  ) {
    setLoading(true);
    const payload = {
      page: page,
      search: search,
      filter: filter,
    };
    const callResult = await makePostApiCall<ProductCategoriesResponse, typeof payload>(
      `products/categories`,
      payload
    );
    setLoading(false);
    if (callResult.success) {
      setProductCategories(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch product categories.");
    }
  };

  const getPrimaryCategoriesForDropdown = async function () {
    const callResult = await makePostApiCall<SelectOption[]>(`products/categories/dropdown`);
    if (callResult.success) {
      setPrimaryCategoriesForDropdown(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data?.message || "Could not fetch categories");
    }
  };

  const resetFilters = () => {
    const filterReset = {
      primaryCategory: { value: -1, label: "All Primary Categories" },
    };
    setFilters(filterReset);
    getProductCategories(null, 1, filterReset);
  };

  const deleteCategory = async function (id: number) {
    const callResult = await makePostApiCall<ApiMessage>(`products/categories/delete/${id}`, {});
    if (callResult.success) {
      showSuccess(callResult.data?.message || "Category deleted");
      getProductCategories(null, 1, filters);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal(
          "Could not delete category",
          callResult.data?.message || "Please try again."
        );
      }
    }
  };

  const askDeleteCategory = function (category: ProductCategory) {
    if (category.is_default) {
      showErrorModal(
        "Default category cannot be deleted",
        "Select another category as the tenant default before deleting this category."
      );
      return;
    }

    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          title="Delete category"
          buttonMessage="Delete"
          message={`Are you sure you want to delete "${category.name}"? This action cannot be undone.`}
          setOpen={setEmptyModelOpen}
          confirmAction={async () => {
            await deleteCategory(category.id);
            setEmptyModelOpen({ childComponent: null, open: false });
          }}
        />
      ),
      open: true,
    });
  };

  const primaryCategoryOptions = [
    { value: -1, label: "All Primary Categories" },
    ...primaryCategoriesForDropdown,
  ];

  const filterComponents = [
    <FilterSingleSelectDropdown
      key="primaryCategory"
      header="Primary Category"
      onChange={(value) =>
        setFilters({
          ...filters,
          primaryCategory: value ?? defaultFilters.primaryCategory,
        })
      }
      options={primaryCategoryOptions}
      resetFilter={() => setFilters(defaultFilters)}
      valueName="primaryCategory"
      value={filters.primaryCategory}
    />,
  ];

  return {
    emptyModal,
    setEmptyModelOpen,
    getProductCategories,
    loading,
    primaryCategories,
    setSlideOver,
    slideOver,
    filterComponents,
    resetFilters,
    filters,
    getPrimaryCategoriesForDropdown,
    askDeleteCategory,
  };
}
